/**
 * Runs a format in the background worker (format.worker.ts), one worker for the
 * tab, started on the first format.
 *
 * The main thread runs the same code only where a worker can't run at all: no
 * Worker (tests), or one that fails before it ever answered. A format that fails
 * IN the worker — an error, or a crash (out of memory on a pathological file or
 * config) — is reported as a failed format and never retried on the main thread,
 * which would freeze the tab on exactly the input the worker is there to isolate.
 * A crashed worker is replaced on the next format, and so is one that HANGS
 * (an endless loop never fires `onerror`): a request unanswered after
 * FORMAT_TIMEOUT_MS fails, and the worker is stopped — or every later format
 * would queue behind it until the page reloads.
 */
import { formatAndDiff, type FormatOutcome, type FormatRequest } from './format-core.js'

/** How long a format may take in the worker. Python's covers the first format's ~3.6 MB Ruff download on a slow link. */
export const FORMAT_TIMEOUT_MS = 30_000
export const PYTHON_FORMAT_TIMEOUT_MS = 180_000

let worker: Worker | null | undefined
/** The worker has answered at least once: from then on a failure is the format's, not the platform's. */
let workerAnswered = false
let nextId = 0
const pending = new Map<number, { resolve: (o: FormatOutcome) => void; reject: (e: Error) => void; python: boolean }>()

function failAll(err: Error) {
  for (const p of pending.values()) p.reject(err)
  pending.clear()
}

class WorkerUnavailable extends Error {}
class FormatFailure extends Error {
  constructor(readonly kind: 'timeout' | 'crashed') { super(kind) }
}

function getWorker(): Worker | null {
  if (worker !== undefined) return worker
  workerAnswered = false
  try {
    worker = typeof Worker === 'undefined' ? null : new Worker(new URL('./format.worker.ts', import.meta.url), { type: 'module' })
  } catch {
    worker = null
  }
  const self = worker
  if (self) {
    // Each handler answers for ITS worker only: a replaced one (timed out) must not touch the next.
    self.onmessage = (e: MessageEvent<{ id: number; outcome?: FormatOutcome; error?: string }>) => {
      if (worker !== self) return
      workerAnswered = true
      const p = pending.get(e.data.id)
      if (!p) return
      pending.delete(e.data.id)
      if (e.data.outcome) p.resolve(e.data.outcome)
      else p.reject(new Error(e.data.error ?? 'format failed'))
    }
    self.onerror = (e) => {
      e.preventDefault()
      if (worker !== self) return
      self.terminate()
      if (workerAnswered) {
        // It worked before: this format broke it. Start a fresh one next time.
        worker = undefined
        failAll(new FormatFailure('crashed'))
      } else {
        // It never loaded: the main thread takes over for this tab.
        worker = null
        failAll(new WorkerUnavailable('format worker unavailable'))
      }
    }
  }
  return worker
}

export async function runFormat(req: FormatRequest): Promise<FormatOutcome> {
  const w = getWorker()
  if (!w) return formatAndDiff(req)
  const id = ++nextId
  try {
    return await new Promise<FormatOutcome>((resolve, reject) => {
      const timer = setTimeout(() => {
        // A timeout means the worker is stuck — stop it, fail what it holds, start fresh next time — unless an
        // older Python format is still inside its longer budget (its first run downloads Ruff): then this one
        // may only have waited behind it, and fails alone.
        const oldest = pending.entries().next().value
        if (!oldest || oldest[0] === id || !oldest[1].python) {
          if (worker === w) { w.terminate(); worker = undefined }
          failAll(new FormatFailure('timeout'))
        } else {
          pending.get(id)?.reject(new FormatFailure('timeout'))
          pending.delete(id)
        }
      }, req.language === 'python' ? PYTHON_FORMAT_TIMEOUT_MS : FORMAT_TIMEOUT_MS)
      pending.set(id, {
        resolve: (o) => { clearTimeout(timer); resolve(o) },
        reject: (e) => { clearTimeout(timer); reject(e) },
        python: req.language === 'python',
      })
      w.postMessage({ id, ...req })
    })
  } catch (err) {
    if (err instanceof WorkerUnavailable) return formatAndDiff(req)
    // The formatter failed, not the code: an unexpected error in the worker counts as it stopping.
    return { ok: false, reason: 'failed', cause: err instanceof FormatFailure ? err.kind : 'crashed' }
  }
}
